import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserCheck, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { COMPANY } from "../data/company";
import { track } from "../lib/analytics";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-black/10 bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#1A3B2B]/30 text-sm";
const labelCls = "block text-xs uppercase tracking-widest text-muted-foreground mb-1.5";

export const EnquiryForm = ({ destination = "", sourceTripId = null, startDate = "", partySize = "", heading = "Plan this with a human" }) => {
  const [form, setForm] = useState({
    name: "", email: "", destination, start_date: startDate || "", end_date: "", party_size: partySize || 2, budget_aud: "", notes: "", website: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/enquiries", {
        ...form,
        party_size: Number(form.party_size) || 1,
        budget_aud: form.budget_aud === "" ? null : Number(form.budget_aud),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        source_trip_id: sourceTripId || null,
      });
      setStatus("sent");
      track("enquiry_submitted", { destination: form.destination, has_trip: Boolean(sourceTripId) });
      toast.success("Enquiry sent — we'll be in touch within 48 hours");
    } catch (err) {
      setStatus("failed");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't send your enquiry right now.");
    }
  };

  if (status === "sent")
    return (
      <section className="rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-8 sm:p-10 text-center" data-testid="enquiry-success">
        <UserCheck className="w-10 h-10 mx-auto mb-3 text-[#C8553D]" strokeWidth={1.5} />
        <h2 className="font-display text-3xl mb-2">Thanks, {form.name.split(" ")[0] || "traveller"}.</h2>
        <p className="opacity-85 max-w-md mx-auto">
          A human will review your trip and reply to {form.email} within 48 hours. A confirmation is on its way to your inbox.
        </p>
      </section>
    );

  return (
    <section className="rounded-3xl border border-black/5 bg-white/60 p-6 sm:p-10" data-testid="enquiry-form-section">
      <div className="flex items-start gap-4 mb-6">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1A3B2B] text-[#FDFBF7] shrink-0">
          <UserCheck className="w-6 h-6" strokeWidth={1.5} />
        </span>
        <div>
          <h2 className="font-display text-3xl leading-tight">{heading}</h2>
          <p className="text-muted-foreground mt-1 max-w-xl">
            Want a real person to check every booking, opening hour and price? Tell us about your trip and we'll come back within 48 hours.{" "}
            <Link to="/plan-with-us" className="text-[#C8553D] underline underline-offset-4" data-testid="enquiry-learn-more">What's included</Link>
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name</label><input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="enquiry-name" /></div>
          <div><label className={labelCls}>Email</label><input required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="enquiry-email" /></div>
        </div>
        <div><label className={labelCls}>Destination</label><input required minLength={2} value={form.destination} onChange={set("destination")} placeholder="Where are you heading?" className={inputCls} data-testid="enquiry-destination" /></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><label className={labelCls}>Start date</label><input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="enquiry-start-date" /></div>
          <div><label className={labelCls}>End date</label><input type="date" value={form.end_date} min={form.start_date || undefined} onChange={set("end_date")} className={inputCls} data-testid="enquiry-end-date" /></div>
          <div><label className={labelCls}>Travellers</label><input required type="number" min={1} max={50} value={form.party_size} onChange={set("party_size")} className={inputCls} data-testid="enquiry-party-size" /></div>
          <div><label className={labelCls}>Budget (AUD)</label><input type="number" min={0} step={50} value={form.budget_aud} onChange={set("budget_aud")} placeholder="e.g. 5000" className={inputCls} data-testid="enquiry-budget" /></div>
        </div>
        <div><label className={labelCls}>Anything we should know?</label><textarea rows={4} maxLength={3000} value={form.notes} onChange={set("notes")} placeholder="Occasion, must-sees, mobility needs, dietary requirements…" className={inputCls} data-testid="enquiry-notes" /></div>
        <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" data-testid="enquiry-honeypot" />
        {status === "failed" && (
          <p className="text-sm text-[#C8553D] p-3 rounded-xl bg-[#C8553D]/10" data-testid="enquiry-error">
            {error} You can also email us at{" "}
            <a href={`mailto:${COMPANY.email}`} className="underline underline-offset-4 font-medium">{COMPANY.email}</a>.
          </p>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
          <button type="submit" disabled={status === "sending"} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8553D] text-[#FDFBF7] text-sm hover:bg-[#a8412f] disabled:opacity-60 transition-colors" data-testid="enquiry-submit">
            {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request a human-verified plan
          </button>
          <p className="text-xs text-muted-foreground">
            48-hour turnaround · full refund if not delivered in 5 business days · <Link to="/refunds" className="underline underline-offset-4">refund policy</Link>
          </p>
        </div>
      </form>
    </section>
  );
};
